import classNames from 'classnames';
import styles from './VehicleSessionHistory.module.css';
import { Table, ConfigProvider, Spin, Button, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { useState, useEffect } from 'react';
import { getVehicleStatusModuleHistory } from 'src/app/query.service';
import axios from 'axios';

interface Data {
  [key: string]: any;
}

export interface DataType {
  sessionDateTime: string;
  importDateTime: string;
  sourceType: string;
  dealerName: string;
  dealerTopixCode: string;
  dealerTopixCountry: string;
  dealerPAGCode: string;
  dealerPAGCountry: string;
  odokm: string;
  odomiles: string;
  diagnosticSoftwareVersion: string;
  diagnosticComputerType: string;
  diagnosticComputerID: string;
  sessionID: string;
}

interface SessionHistoryProps {
  searchedVin: string;
}
type FileType = '.zip' | '.csv';
type QueryType = 'session' | 'someOtherType';

interface QueryParameter {
  sessionId: string;
  sessionTimestamp: string;
}

const VehicleSessionHistory: React.FC<SessionHistoryProps> = ({ searchedVin }) => {
  const [data, setData] = useState<Data[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDownloadPending, setIsDownloadPending] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState<Data | null>(null);
  const handleRowClick = (record: Data) => {
    setSelectedSession(record);
    setModalVisible(true);
  };
  const getDownloadUrl = (
    fileType: FileType,
    queryType: QueryType,
    // userId: string,
    offset?: number
  ): string => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/viewer/${queryType}`;

    if (queryType === 'session') {
      baseUrl += fileType === '.zip' ? '/summary/export' : '/export';
    } else {
      baseUrl = `${process.env.REACT_APP_API_URL}/${queryType}/query/final/export`;
    }

    // Construct query parameters
    const params = new URLSearchParams();

    // if (userId) {
    //   params.append('userId', userId);
    // }

    if (offset !== undefined) {
      params.append('offset', offset.toString());
    }

    // Append query parameters if they exist
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  };

  const getSddSessionInfo = async (
    queryParameter: { sessionId: string; sessionTimestamp: string },
    // userId: string,
    token: string
  ): Promise<string> => {
    const downloadUrl = `${process.env.REACT_APP_API_URL}/viewer/session/info`;
    // if (userId) {
    //   downloadUrl += `?userId=${encodeURIComponent(userId)}`;
    // }

    const response = await axios.post(downloadUrl, queryParameter, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      responseType: 'text',
    });

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to fetch session info');
    }
  };

  const handleDownloadZip = async () => {
    if (!selectedSession) return;

    // const userId: string = localStorage.getItem('userId') || '';
    const token: string | null = localStorage.getItem('id_token');

    if (!token) {
      console.error('No auth token found!');
      return;
    }

    if (isDownloadPending) return;
    setIsDownloadPending(true);

    try {
      const downloadUrl: string = getDownloadUrl('.zip', 'session');

      const payload = {
        sessionId: selectedSession.sessionId,
        sessionTimestamp: selectedSession.sessionTimestamp,
      };

      const response = await axios.post(downloadUrl, payload, {
        responseType: 'blob', // Ensures the response is treated as a file
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      let fileName = 'download.zip';
      const contentDisposition = response.headers['content-disposition'];

      if (contentDisposition) {
        console.log('Content-Disposition Header:', contentDisposition); // Debugging

        const filenameRegex = /filename=["']?([^"';]+)["']?/;
        const matches = contentDisposition.match(filenameRegex);

        if (matches && matches[1]) {
          fileName = matches[1]; // Correctly extracts "example.zip"
        }
      }

      // Handle file download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading the file:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsDownloadPending(false);
    }
  };
  const handleViewSession = async () => {
    if (!selectedSession) return;

    setIsLoading(true);

    // const userId: string = decodeURIComponent(localStorage.getItem('userId')) || '';
    const token: string | null = localStorage.getItem('id_token');

    if (!token) {
      console.error('No auth token found!');
      setIsLoading(false);
      return;
    }

    const queryParameter = {
      sessionId: selectedSession.sessionId,
      sessionTimestamp: selectedSession.sessionTimestamp,
    };

    try {
      const sddSummaryHTML = await getSddSessionInfo(queryParameter, token);

      // Open a new window and display the session info
      const windowRef = window.open('/', '_blank', 'scrollbars,resizable,height=800,width=1325');
      if (windowRef) {
        const sessionViewerPlaceholder = `<div style='overflow-y:auto;overflow-x:hidden;height:100%;width:100%'>${sddSummaryHTML}</div>`;
        windowRef.document.write(sessionViewerPlaceholder);
      }
    } catch (err) {
      console.error('Error fetching session info:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const columns: ColumnsType<Data> = [
    {
      title: 'Session Date/Time',
      dataIndex: 'sessionTimestampString',
      key: 'sessionTimestampString',
      fixed: 'left',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Import Date/Time',
      dataIndex: 'importTimestampString',
      key: 'importTimestampString',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Source Type',
      dataIndex: 'sourceType',
      key: 'sourceType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Dealer Name',
      dataIndex: 'dealerName',
      key: 'dealerName',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Dealer Topix Code',
      dataIndex: 'dealerTopixCode',
      key: 'dealerTopixCode',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Dealer Topix Country',
      dataIndex: 'dealerTopixCountry',
      key: 'dealerTopixCountry',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Dealer PAG Code',
      dataIndex: 'dealerPagCode',
      key: 'dealerPagCode',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Dealer PAG Country',
      dataIndex: 'dealerPagCountry',
      key: 'dealerPagCountry',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'ODO (km)',
      dataIndex: 'odoKm',
      key: 'odoKm',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'ODO (miles)',
      dataIndex: 'odoMiles',
      key: 'odoMiles',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Diagnostic Software Version',
      dataIndex: 'diagnosticSoftwareVersion',
      key: 'diagnosticSoftwareVersion',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Diagnostic Computer Type',
      dataIndex: 'diagnosticComputerType',
      key: 'diagnosticComputerType',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Diagnostic Computer ID',
      dataIndex: 'diagnosticComputerId',
      key: 'diagnosticComputerId',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: '100px',
      onHeaderCell: () => {
        return { style: { minWidth: '100px' } }; // Set min-width for the column
      },
    },
  ];

  const getSessionHistory = async () => {
    const userId = localStorage.getItem('userId');
    try {
      setIsLoading(true);
      const result: any = await getVehicleStatusModuleHistory('session', searchedVin);
      result.items.forEach((n: any) => {
        n.sessionTimestampString = new Date(n.sessionTimestamp).toLocaleString();
        n.importTimestampString = new Date(n.importTimestamp).toLocaleString();
      });

      const sortedData = result.items.sort((a: Data, b: Data) => {
        return new Date(b.sessionTimestamp).getTime() - new Date(a.sessionTimestamp).getTime();
      });

      setData([...sortedData]);
      setIsLoading(false);
    } catch (err) {
      console.log('error...', err);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    getSessionHistory();
  }, []);
  let clipboardMessageShown = false;
  useEffect(() => {
    const handleMouseUp = () => {
      const selectedText = window.getSelection()?.toString();
      if (selectedText && selectedText.trim().length > 0) {
        navigator.clipboard
          .writeText(selectedText.trim())
          .then(() => {
            if (!clipboardMessageShown) {
              clipboardMessageShown = true;
              message.success('Copied to clipboard', 1.5);
              setTimeout(() => {
                clipboardMessageShown = false;
              }, 1500);
            }
          })
          .catch(() => {
            message.error('Failed to copy');
          });
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  const tableTheme = {
    components: {
      Table: {
        headerBg: '#dcdcdc',
        headerSplitColor: 'white',
        headerBorderRadius: 0,
        cellFontSize: 14,
      },
    },
  };

  // TODO: Implement logic to populate data in the table with api call
  return (
    <div className={classNames(styles.VehicleSessionHistory)}>
      <ConfigProvider theme={tableTheme}>
        <Table
          columns={columns}
          dataSource={data}
          scroll={{ x: 'max-content', y: 300 }}
          pagination={false}
          rowKey="id"
          loading={{
            spinning: isLoading,
            indicator: <Spin className={classNames(styles['loader-color'])} />,
          }}
          onRow={(record) => ({
            onClick: (e) => {
              const selection = window.getSelection();
              const isTextSelected = selection && selection.toString().length > 0;

              if (!isTextSelected) {
                handleRowClick(record);
              }
            },
          })}
        />
      </ConfigProvider>

      <Modal
        title="Session"
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedSession && (
          <div>
            <p>{selectedSession.sessionId}</p>
            {selectedSession.sourceType == 'Topix' ? (
              <div className={classNames(styles['VehicleSessionHistory--topix-notes-container'])}>
                <p>
                  TOPIX data cannot currently be downloaded in VFDB, please use{' '}
                  <a href="https://topix.jaguar.jlrext.com" target="_blank" rel="noreferrer">
                    topix.jaguar.jlrext.com
                  </a>
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                <Button
                  onClick={handleViewSession}
                  className={classNames(styles['VehicleSessionHistory--Buttons'])}
                >
                  View Session
                </Button>
                <Button
                  onClick={handleDownloadZip}
                  className={classNames(styles['VehicleSessionHistory--Buttons'])}
                  style={{ marginRight: 20 }}
                >
                  Download ZIP
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default VehicleSessionHistory;
